import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { AnnotationPlane } from '~/domain/annotation/annotation';
import {
    createRegionId,
    IntensityRegion,
    IntensityRegionDraft,
    IntensityRegionShape,
    regionBetween,
    RegionGeometry,
    regionHasArea,
    regionInFrame,
} from '~/domain/intensity/intensityRegion';
import { PointMm } from '~/domain/geometry/patientGeometry';
import { MEASURE_CURSORS } from '~/engine/data/common/customs/cursors/measurementCursor';
import { regionIntensityStatistics } from '~/engine/data/image/regionIntensityStatistics';
import type { ShownPaneImage } from '~/engine/view/paneImage';
import {
    paneToImageFrame,
    pinToViewPlane,
    viewPlaneCoordinateMm,
    viewShowsPlane,
} from '~/engine/view/planeViewGeometry';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import type { Image3D } from '~/domain/patient/image3d';
import { localization } from '~/i18n';
import { AnnotationPlacementTool } from './AnnotationPlacementTool';

/**
 * Shown when a region is pressed on a tile whose image is not whole, with
 * the counts of that image: figures read from the slices present would
 * look like the region's own (SR-WOR-H-37-3). The words are the reason a
 * disabled tool gives for the same image state.
 */
function regionImageNotWholeMessage(image: Image3D): string {
    const total = image.expectedSliceCount;
    return image.isIncompleteFinal
        ? localization.t('tool.disabled.imageIncomplete', {
              unavailable: image.unavailableSliceCount,
              total,
          })
        : localization.t('tool.disabled.imageLoading', {
              loaded: image.loadedSliceCount,
              total,
          });
}

/** The placement in the pane's frame, with the pane it was started on. */
interface ActiveDraft {
    readonly pane: ShownPaneImage;
    readonly plane: AnnotationPlane;
    /** The first click: a circle's centre, or a rectangle's corner. */
    readonly anchorMm: PointMm;
    /** The view's in-plane axes when placing began; a rectangle's sides run along them. */
    readonly axes: readonly [ReadonlyVec3, ReadonlyVec3];
    geometry: RegionGeometry;
}

/**
 * Places a region of one shape. For a circle the first click sets the centre
 * and the second a point on its edge; for a rectangle the clicks set opposite
 * corners, its sides along the tile's screen axes. Between the clicks the
 * region follows the cursor; releasing a press dragged clear of where it
 * started places the second point too. The click lifecycle is in
 * {@link AnnotationPlacementTool}.
 *
 * On commit the voxels inside the region are read once, from the image the
 * pane is showing, whose grid is in the pane's frame. The region is then
 * stored in that image's own frame.
 */
export class IntensityRegionTool extends AnnotationPlacementTool {
    public readonly toolType = 'intensityRegion';

    private readonly _shape: IntensityRegionShape;
    private readonly _onDraftChanged: (
        draft: IntensityRegionDraft | null
    ) => void;
    private readonly _onCommit: (region: IntensityRegion) => void;

    private _draft: ActiveDraft | null = null;

    public constructor(
        shape: IntensityRegionShape,
        onDraftChanged: (draft: IntensityRegionDraft | null) => void,
        onCommit: (region: IntensityRegion) => void
    ) {
        super();
        this._shape = shape;
        this._onDraftChanged = onDraftChanged;
        this._onCommit = onCommit;
    }

    public override get cursor(): string {
        return MEASURE_CURSORS[this._shape];
    }

    /**
     * A tile whose image is not whole is refused before any drafting, with
     * that image's own progress: the Measure tool's region refusal follows
     * the sidebar image, which need not be the one the pressed tile shows.
     */
    protected override placementRefusal(
        _view: PlaneView,
        pane: ShownPaneImage
    ): string | null {
        const image = pane.image.parent;
        return image.isComplete ? null : regionImageNotWholeMessage(image);
    }

    protected override placesPointOnDragRelease(): boolean {
        return true;
    }

    /**
     * An off-image first point is accepted, unlike a line profile's endpoint:
     * a region over the image edge still measures the voxels that exist, and
     * one over nothing reports "no data".
     */
    protected override startPlacement(
        view: PlaneView,
        plane: AnnotationPlane,
        pointMm: PointMm,
        pane: ShownPaneImage
    ): boolean {
        const axes = [
            vec3.clone(view.viewXDirection),
            vec3.clone(view.viewYDirection),
        ] as const;
        this._draft = {
            pane,
            plane,
            anchorMm: pointMm,
            axes,
            geometry: regionBetween(this._shape, pointMm, pointMm, ...axes),
        };
        this.emitDraft();
        return true;
    }

    protected override placePoint(view: PlaneView, pointMm: PointMm): void {
        const draft = this._draft;
        if (!draft) {
            return;
        }
        // The base has checked that the pane still shows the image of the
        // first click. The statistics below also need the view on the
        // region's plane and that image whole: abandon rather than read
        // values that are not the region's own.
        const { pane } = draft;
        if (
            !viewShowsPlane(view, draft.plane) ||
            !pane.image.parent.isComplete
        ) {
            this.cancelDraftForChangedImage();
            return;
        }
        const geometry = this.regionTo(view, draft, pointMm);
        // A second click on the first point, or for a rectangle level with
        // it, is a mis-click, not a request for a region with no area:
        // ignore it and keep following the cursor.
        if (!regionHasArea(geometry)) {
            return;
        }
        this._draft = null;
        this.finishPlacement();
        this._onDraftChanged(null);
        this._onCommit(
            regionInFrame(
                {
                    ...geometry,
                    id: createRegionId(this._shape),
                    plane: draft.plane,
                    visible: true,
                    stats: regionIntensityStatistics(
                        pane.image,
                        geometry,
                        draft.plane.viewDirection
                    ),
                },
                paneToImageFrame(pane)
            )
        );
    }

    protected override moveCursor(view: PlaneView, pointMm: PointMm): void {
        if (!this._draft) {
            return;
        }
        this._draft.geometry = this.regionTo(view, this._draft, pointMm);
        this.emitDraft();
    }

    protected override undoPoint(): void {
        this.cancelDraft();
    }

    protected override lastPlacedPoint(): PointMm | null {
        return this._draft?.anchorMm ?? null;
    }

    protected override discardDraft(): void {
        this._draft = null;
        this._onDraftChanged(null);
    }

    /**
     * The region from the first point to the cursor, which is first moved
     * onto the plane through the first point, so scrolling to another slice
     * between the two clicks cannot make the region larger.
     */
    private regionTo(
        view: PlaneView,
        { anchorMm, axes }: ActiveDraft,
        pointMm: PointMm
    ): RegionGeometry {
        const inPlane = pinToViewPlane(
            pointMm,
            view,
            viewPlaneCoordinateMm(anchorMm, view)
        );
        return regionBetween(this._shape, anchorMm, inPlane, ...axes);
    }

    private emitDraft(): void {
        if (!this._draft) {
            return;
        }
        this._onDraftChanged(
            regionInFrame(
                { ...this._draft.geometry, plane: this._draft.plane },
                paneToImageFrame(this._draft.pane)
            )
        );
    }
}
